import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Loader2, ShieldAlert, Users, DollarSign, Calendar, UserCheck, Pause, Trash2, Eye, X, Search, Ban,
  EyeOff, Star, Pencil, ShieldCheck, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  adminDashboardStats, adminListCoaches, adminGetCoach,
  adminSetCoachStatus, adminDeleteCoach, adminListClients,
  adminSetClientStatus, adminDeleteClient,
  adminEditCoach, adminSetCoachVisibility, adminSetCoachFeature,
  adminSetCoachVerification, adminBanCoach, adminBanClient,
} from '../lib/api';
import { formatCurrency, getInitials } from '../lib/utils';
import AdminApplications from '../components/AdminApplications';
import { AdminBans, AdminClaims } from '../components/AdminBansClaims';
import SEO from '../components/SEO';

const StatusBadge = ({ status }) => {
  const map = {
    approved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    suspended: 'bg-red-500/15 text-red-300 border-red-500/30',
    banned: 'bg-red-500/25 text-red-200 border-red-500/50',
  };
  return <Badge className={`${map[status] || 'bg-zinc-700 text-zinc-300'} text-xs`}>{status || 'approved'}</Badge>;
};

const StatCard = ({ Icon, label, value, accent = '#C0A062', testid }) => (
  <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</p>
      <Icon className="w-4 h-4" style={{ color: accent }} />
    </div>
    <p className="text-2xl font-bold text-white mt-2">{value}</p>
  </div>
);

const CoachDetail = ({ coach, onClose, onAction, onEdit, busy }) => {
  const tp = coach.user?.trainer_profile || {};
  const isHidden = !!tp.is_hidden;
  const isFeatured = !!tp.is_featured;
  const isBanned = !!coach.user?.banned;
  return (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-4" onClick={onClose}>
    <div
      className="bg-[#18181B] border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
      data-testid="coach-detail-modal"
    >
      <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-[#18181B] z-10">
        <h3 className="font-semibold text-white">{coach.user.full_name}</h3>
        <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="coach-detail-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5 space-y-5">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 border-2 border-[#C0A062]/40">
            <AvatarImage src={tp.profile_image} />
            <AvatarFallback className="bg-[#27272A] text-[#C0A062]">{getInitials(coach.user.full_name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="text-white font-semibold">{coach.user.full_name}</p>
            <p className="text-xs text-zinc-500">{coach.user.email} · {coach.user.city || '—'}</p>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <StatusBadge status={tp.status} />
              {isFeatured && (
                <Badge className="bg-[#C0A062]/20 text-[#C0A062] border border-[#C0A062]/40 text-[10px]">Featured</Badge>
              )}
              {isHidden && (
                <Badge className="bg-zinc-700 text-zinc-300 border border-zinc-600 text-[10px]">Hidden</Badge>
              )}
              {isBanned && (
                <Badge className="bg-red-500/20 text-red-300 border border-red-500/40 text-[10px]">Banned</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-zinc-800/40 rounded-lg p-3">
            <p className="text-[10px] uppercase text-zinc-500">Sessions</p>
            <p className="text-lg font-bold text-white">{coach.stats.sessions_completed}</p>
          </div>
          <div className="bg-zinc-800/40 rounded-lg p-3">
            <p className="text-[10px] uppercase text-zinc-500">Earnings</p>
            <p className="text-lg font-bold text-[#C0A062]">{formatCurrency(coach.stats.total_earnings)}</p>
          </div>
          <div className="bg-zinc-800/40 rounded-lg p-3">
            <p className="text-[10px] uppercase text-zinc-500">Reviews</p>
            <p className="text-lg font-bold text-white">{coach.stats.review_count}</p>
          </div>
        </div>

        {tp.bio && (
          <div>
            <p className="text-xs uppercase text-zinc-500 mb-1">Bio</p>
            <p className="text-sm text-zinc-300 whitespace-pre-line">{tp.bio}</p>
          </div>
        )}

        {(tp.certifications || []).length > 0 && (
          <div>
            <p className="text-xs uppercase text-zinc-500 mb-1">Credentials</p>
            <ul className="text-sm text-zinc-300 list-disc list-inside">
              {tp.certifications.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <p className="text-xs uppercase text-zinc-500">PT rate</p>
            <p className="text-zinc-300">{tp.price_personal_training ? `$${tp.price_personal_training}` : '—'}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-zinc-500">SD rate</p>
            <p className="text-zinc-300">{tp.price_self_defense ? `$${tp.price_self_defense}` : '—'}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-zinc-500">BJJ rate</p>
            <p className="text-zinc-300">{tp.price_jiu_jitsu ? `$${tp.price_jiu_jitsu}` : '—'}</p>
          </div>
        </div>

        {/* Primary lifecycle actions */}
        <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-800">
          <Button size="sm" onClick={() => onAction('approved')} disabled={busy} className="bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/40" data-testid="coach-action-approve">
            <UserCheck className="w-4 h-4 mr-1" /> Approve
          </Button>
          <Button size="sm" onClick={() => onAction('suspended')} disabled={busy} className="bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/40" data-testid="coach-action-suspend">
            <Pause className="w-4 h-4 mr-1" /> Suspend
          </Button>
          <Button size="sm" onClick={() => onEdit()} disabled={busy} className="bg-zinc-700/40 text-zinc-200 hover:bg-zinc-700/60 border border-zinc-600" data-testid="coach-action-edit">
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
          <Button size="sm" onClick={() => onAction(isBanned ? 'unban' : 'ban')} disabled={busy} className="bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/40" data-testid="coach-action-ban">
            <Ban className="w-4 h-4 mr-1" /> {isBanned ? 'Unban' : 'Ban'}
          </Button>
          <Button size="sm" onClick={() => onAction('delete')} disabled={busy} className="bg-red-500/15 text-red-300 hover:bg-red-500/25 border border-red-500/40" data-testid="coach-action-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>

        {/* Display controls */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAction(isHidden ? 'show' : 'hide')}
            disabled={busy}
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
            data-testid="coach-action-toggle-visibility"
          >
            {isHidden ? <><Eye className="w-4 h-4 mr-1" /> Show</> : <><EyeOff className="w-4 h-4 mr-1" /> Hide</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAction(isFeatured ? 'unfeature' : 'feature')}
            disabled={busy}
            className={`border-zinc-700 text-zinc-200 hover:bg-zinc-800 ${isFeatured ? 'bg-[#C0A062]/10 border-[#C0A062]/40 text-[#C0A062]' : ''}`}
            data-testid="coach-action-toggle-feature"
          >
            <Star className={`w-4 h-4 mr-1 ${isFeatured ? 'fill-current' : ''}`} /> {isFeatured ? 'Unfeature' : 'Feature'}
          </Button>
        </div>

        {/* Verification select */}
        <div>
          <p className="text-xs uppercase text-zinc-500 mb-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Verification level</p>
          <div className="flex flex-wrap gap-2">
            {['verified', 'elite', 'specialist'].map((lvl) => (
              <Button
                key={lvl}
                size="sm"
                variant="outline"
                onClick={() => onAction(`verify:${lvl}`)}
                disabled={busy}
                className={`capitalize border-zinc-700 text-zinc-200 hover:bg-zinc-800 ${tp.verification_level === lvl ? 'bg-[#C0A062]/10 border-[#C0A062]/40 text-[#C0A062]' : ''}`}
                data-testid={`coach-verify-${lvl}`}
              >
                <Award className="w-3.5 h-3.5 mr-1" /> {lvl}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

const CoachEditDialog = ({ coach, open, onOpenChange, onSaved }) => {
  const tp = coach?.user?.trainer_profile || {};
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!coach) return;
    setForm({
      full_name: coach.user.full_name || '',
      email: coach.user.email || '',
      city: coach.user.city || '',
      phone: coach.user.phone || '',
      bio: tp.bio || '',
      price_personal_training: tp.price_personal_training || '',
      price_self_defense: tp.price_self_defense || '',
      price_jiu_jitsu: tp.price_jiu_jitsu || '',
      verification_level: tp.verification_level || 'verified',
      admin_notes: tp.admin_notes || '',
    });
  }, [coach?.user?.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!coach) return;
    setSaving(true);
    try {
      const payload = { ...form };
      ['price_personal_training', 'price_self_defense', 'price_jiu_jitsu'].forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      await adminEditCoach(coach.user.id, payload);
      toast.success('Coach updated');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (!coach) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181B] border-zinc-800 max-w-lg text-white" data-testid="coach-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Edit {coach.user.full_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
          <div>
            <Label className="text-zinc-300 text-xs">Full name</Label>
            <Input value={form.full_name || ''} onChange={(e) => set('full_name', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-name" />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Email</Label>
            <Input value={form.email || ''} onChange={(e) => set('email', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300 text-xs">City</Label>
              <Input value={form.city || ''} onChange={(e) => set('city', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-city" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Phone</Label>
              <Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-phone" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Bio</Label>
            <Textarea value={form.bio || ''} onChange={(e) => set('bio', e.target.value)} rows={3} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-bio" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-zinc-300 text-xs">PT $</Label>
              <Input type="number" value={form.price_personal_training} onChange={(e) => set('price_personal_training', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-price-pt" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">SD $</Label>
              <Input type="number" value={form.price_self_defense} onChange={(e) => set('price_self_defense', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-price-sd" />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">BJJ $</Label>
              <Input type="number" value={form.price_jiu_jitsu} onChange={(e) => set('price_jiu_jitsu', e.target.value)} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-price-bjj" />
            </div>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Verification</Label>
            <Select value={form.verification_level || 'verified'} onValueChange={(v) => set('verification_level', v)}>
              <SelectTrigger className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-verify">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-zinc-800">
                <SelectItem value="verified">Verified</SelectItem>
                <SelectItem value="elite">Elite</SelectItem>
                <SelectItem value="specialist">Specialist</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Admin notes (private)</Label>
            <Textarea value={form.admin_notes || ''} onChange={(e) => set('admin_notes', e.target.value)} rows={2} className="bg-zinc-800/50 border-zinc-700 text-white mt-1" data-testid="edit-coach-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 text-zinc-200 hover:bg-zinc-800" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="edit-coach-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminDashboard = () => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [clients, setClients] = useState([]);
  const [coachQuery, setCoachQuery] = useState('');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, cl] = await Promise.all([
        adminDashboardStats(), adminListCoaches(), adminListClients(),
      ]);
      setStats(s.data); setCoaches(c.data || []); setClients(cl.data || []);
    } catch (e) {
      toast.error('Could not load admin data');
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const openCoach = async (row) => {
    try {
      const res = await adminGetCoach(row.id);
      setSelectedCoach(res.data);
    } catch (e) {
      toast.error('Could not load coach');
    }
  };

  const refreshSelectedCoach = async () => {
    if (!selectedCoach) return;
    try {
      const res = await adminGetCoach(selectedCoach.user.id);
      setSelectedCoach(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  const handleAction = async (action) => {
    if (!selectedCoach) return;
    const id = selectedCoach.user.id;
    setBusy(true);
    try {
      if (action === 'delete') {
        if (!window.confirm(`Remove ${selectedCoach.user.full_name}? This cannot be undone.`)) {
          setBusy(false); return;
        }
        await adminDeleteCoach(id);
        toast.success('Coach removed');
        setSelectedCoach(null);
        load();
        return;
      }
      if (action === 'ban') {
        const reason = window.prompt(`Ban ${selectedCoach.user.full_name}? Enter a reason:`);
        if (!reason || !reason.trim()) { setBusy(false); return; }
        await adminBanCoach(id, true, reason.trim());
        toast.success('Coach banned');
      } else if (action === 'unban') {
        await adminBanCoach(id, false);
        toast.success('Ban lifted');
      } else if (action === 'hide') {
        await adminSetCoachVisibility(id, true);
        toast.success('Coach hidden');
      } else if (action === 'show') {
        await adminSetCoachVisibility(id, false);
        toast.success('Coach visible');
      } else if (action === 'feature') {
        await adminSetCoachFeature(id, true);
        toast.success('Coach featured');
      } else if (action === 'unfeature') {
        await adminSetCoachFeature(id, false);
        toast.success('Feature removed');
      } else if (action?.startsWith('verify:')) {
        const lvl = action.split(':')[1];
        await adminSetCoachVerification(id, lvl);
        toast.success(`Verification: ${lvl}`);
      } else if (action === 'approved' || action === 'suspended' || action === 'pending') {
        await adminSetCoachStatus(id, action);
        toast.success(`Marked ${action}`);
      }
      await refreshSelectedCoach();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleClientAction = async (client, action) => {
    try {
      if (action === 'delete') {
        if (!window.confirm(`Remove ${client.full_name}? This cancels their bookings and cannot be undone.`)) return;
        await adminDeleteClient(client.id);
        toast.success('Client removed');
      } else if (action === 'suspend') {
        await adminSetClientStatus(client.id, false);
        toast.success('Client suspended');
      } else if (action === 'reactivate') {
        await adminSetClientStatus(client.id, true);
        toast.success('Client reactivated');
      } else if (action === 'ban') {
        const reason = window.prompt(`Ban ${client.full_name}? Enter a reason:`);
        if (!reason || !reason.trim()) return;
        await adminBanClient(client.id, true, reason.trim());
        toast.success('Client banned');
      } else if (action === 'unban') {
        await adminBanClient(client.id, false);
        toast.success('Ban lifted');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const filteredCoaches = useMemo(() => {
    if (!coachQuery.trim()) return coaches;
    const q = coachQuery.toLowerCase();
    return coaches.filter((c) =>
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q)
    );
  }, [coaches, coachQuery]);

  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard | DojoDash" description="DojoDash admin: coaches, clients, applications, revenue." />
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <ShieldAlert className="w-6 h-6 text-[#C0A062]" />
          <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
            <StatCard Icon={UserCheck} label="Approved Coaches" value={stats.approved_coaches} testid="stat-coaches" />
            <StatCard Icon={Users} label="Total Clients" value={stats.total_clients} testid="stat-clients" />
            <StatCard Icon={Calendar} label="Sessions Completed" value={stats.total_sessions_completed} testid="stat-sessions" />
            <StatCard Icon={DollarSign} label="Platform Revenue" value={formatCurrency(stats.total_platform_revenue)} testid="stat-revenue" />
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex rounded-md border border-zinc-800 bg-[#18181B] p-1 mb-6">
          {[
            { id: 'overview', label: 'Coaches' },
            { id: 'clients', label: 'Clients' },
            { id: 'applications', label: 'Coach Signups' },
            { id: 'claims', label: 'Points Claims' },
            { id: 'bans', label: 'Banned Users' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm rounded ${tab === t.id ? 'bg-[#C0A062] text-black font-semibold' : 'text-zinc-400 hover:text-white'}`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden" data-testid="admin-coach-table">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
              <Search className="w-4 h-4 text-zinc-500" />
              <Input
                value={coachQuery}
                onChange={(e) => setCoachQuery(e.target.value)}
                placeholder="Search coaches by name, email, city…"
                className="border-0 bg-transparent text-white focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-zinc-500"
                data-testid="admin-coach-search"
              />
              <span className="text-xs text-zinc-500 shrink-0">{filteredCoaches.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-900/50">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3 hidden sm:table-cell">Specialty</th>
                    <th className="px-4 py-3 hidden md:table-cell">Applied</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {filteredCoaches.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No coaches.</td></tr>
                  ) : filteredCoaches.map((c) => (
                    <tr key={c.id} className="hover:bg-zinc-900/40" data-testid={`coach-row-${c.id}`}>
                      <td className="px-4 py-3 text-white">
                        <div className="flex items-center gap-2">
                          <span>{c.full_name}</span>
                          {c.is_featured && <Star className="w-3.5 h-3.5 text-[#C0A062] fill-current" />}
                          {c.is_hidden && <EyeOff className="w-3.5 h-3.5 text-zinc-500" />}
                          {c.banned && <Ban className="w-3.5 h-3.5 text-red-400" />}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-400">{c.email}</td>
                      <td className="px-4 py-3 text-zinc-400 hidden sm:table-cell">{c.specialty || '—'}</td>
                      <td className="px-4 py-3 text-zinc-500 hidden md:table-cell text-xs">{(c.date_applied || '').slice(0, 10)}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.banned ? 'banned' : c.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openCoach(c)} className="text-[#C0A062] hover:bg-[#C0A062]/10" data-testid={`view-coach-${c.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'clients' && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden" data-testid="admin-client-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-900/50">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3 hidden md:table-cell">Joined</th>
                    <th className="px-4 py-3">Sessions</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {clients.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500">No clients.</td></tr>
                  ) : clients.map((c) => (
                    <tr key={c.id} className="hover:bg-zinc-900/40" data-testid={`client-row-${c.id}`}>
                      <td className="px-4 py-3 text-white">{c.full_name}</td>
                      <td className="px-4 py-3 text-zinc-400">{c.email}</td>
                      <td className="px-4 py-3 text-zinc-500 hidden md:table-cell text-xs">{(c.date_joined || '').slice(0, 10)}</td>
                      <td className="px-4 py-3 text-[#C0A062] font-semibold">{c.sessions_booked}</td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs ${c.banned ? 'bg-red-500/25 text-red-200 border-red-500/50' : c.is_active === false ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'}`}>
                          {c.banned ? 'banned' : c.is_active === false ? 'suspended' : 'active'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {c.is_active === false ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-emerald-300 hover:bg-emerald-500/10"
                            onClick={() => handleClientAction(c, 'reactivate')}
                            data-testid={`reactivate-client-${c.id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" /> Reactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-amber-300 hover:bg-amber-500/10"
                            onClick={() => handleClientAction(c, 'suspend')}
                            data-testid={`suspend-client-${c.id}`}
                          >
                            <Pause className="w-4 h-4 mr-1" /> Suspend
                          </Button>
                        )}
                        {c.banned ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-emerald-300 hover:bg-emerald-500/10 ml-1"
                            onClick={() => handleClientAction(c, 'unban')}
                            data-testid={`unban-client-${c.id}`}
                          >
                            <ShieldCheck className="w-4 h-4 mr-1" /> Unban
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-300 hover:bg-red-500/10 ml-1"
                            onClick={() => handleClientAction(c, 'ban')}
                            data-testid={`ban-client-${c.id}`}
                          >
                            <Ban className="w-4 h-4 mr-1" /> Ban
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-300 hover:bg-red-500/10 ml-1"
                          onClick={() => handleClientAction(c, 'delete')}
                          data-testid={`delete-client-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'applications' && <AdminApplications />}
        {tab === 'claims' && <AdminClaims />}
        {tab === 'bans' && <AdminBans />}
      </div>

      {selectedCoach && (
        <CoachDetail
          coach={selectedCoach}
          onClose={() => setSelectedCoach(null)}
          onAction={handleAction}
          onEdit={() => setEditOpen(true)}
          busy={busy}
        />
      )}
      <CoachEditDialog
        coach={selectedCoach}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={async () => { await refreshSelectedCoach(); load(); }}
      />
    </div>
  );
};

export default AdminDashboard;
